// components/ContentEditor/ContentEditor.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../index.css'; // Ensure global styles are imported
import { useAuth0 } from '@auth0/auth0-react';
import { useOrgUser } from '../../context/OrgUserProvider';
import { getObject, getSignedUrl } from '../../api/s3Api';
import { Edit, TextFields } from '@mui/icons-material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Card,
  CardContent,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  Add,
  Delete,
  DragIndicator,
  Videocam,
  Numbers,
  Image as ImageIcon,
  Save,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

// Generate unique ID for droppable areas
const generateId = () => `droppable-${Math.random().toString(36).substr(2, 9)}`;

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`content-tabpanel-${index}`}
      aria-labelledby={`content-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Helper function to generate video embed code
const getVideoEmbedCode = (url, width = '100%', height = 'auto') => {
  if (!url) return null;
  
  // YouTube
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (videoId && videoId[1]) {
      return (
        <iframe
          width={width}
          height={height}
          src={`https://www.youtube.com/embed/${videoId[1]}`}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ borderRadius: '4px' }}
        />
      );
    }
  }
  
  // Vimeo
  if (url.includes('vimeo.com')) {
    const videoId = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (videoId && videoId[1]) {
      return (
        <iframe
          width={width}
          height={height}
          src={`https://player.vimeo.com/video/${videoId[1]}`}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          style={{ borderRadius: '4px' }}
        />
      );
    }
  }
  
  // Direct MP4 or other video files
  if (url.match(/\.(mp4|webm|ogg)$/i)) {
    return (
      <video
        controls
        width={width}
        height={height}
        style={{ borderRadius: '4px' }}
      >
        <source src={url} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    );
  }
  
  return (
    <Typography variant="body2" color="textSecondary">
      Preview not available for this URL
    </Typography>
  );
};

// Media Configuration Dialog with both video library and URL input
const MediaConfigDialog = ({
  open,
  onClose,
  onConfirm,
  type,
  initialImageKey = '',
  initialUrl = '',
  initialWidth = '100%',
  initialHeight = 'auto',
  courseId,
}) => {
  const [width, setWidth] = useState(initialWidth);
  const [height, setHeight] = useState(initialHeight);
  const [selectedVideo, setSelectedVideo] = useState(initialImageKey);
  const [videoUrl, setVideoUrl] = useState(initialUrl);
  const [tabValue, setTabValue] = useState(0);

  const handleConfirm = () => {
    onConfirm({
      imageKey: selectedVideo,
      url: videoUrl,
      width,
      height,
    });
    onClose();
  };

  const selectVideoFromLibrary = (videoKey) => {
    setSelectedVideo(videoKey);
    setVideoUrl(''); // Clear URL when selecting from library
  };

  const handleUrlChange = (url) => {
    setVideoUrl(url);
    setSelectedVideo(''); // Clear library selection when using URL
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Configure {type === 'image' ? 'Image' : 'Video'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {type === 'image' && (
            <>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Select an image from your media library
                </Typography>
              </Box>

              <MediaLibraryBrowser
                courseId={courseId}
                onSelectImage={selectVideoFromLibrary}
                selectedImage={selectedVideo}
                mediaType="image"
              />

              {selectedVideo && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Selected Image:
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedVideo}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {type === 'video' && (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                  <Tab label="Video Library" />
                  <Tab label="External URL" />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Select a video from your media library
                  </Typography>
                </Box>

                <MediaLibraryBrowser
                  courseId={courseId}
                  onSelectImage={selectVideoFromLibrary}
                  selectedImage={selectedVideo}
                  mediaType="video"
                />

                {selectedVideo && (
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Selected Video:
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedVideo}
                    </Typography>
                  </Box>
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <TextField
                  label="Video URL"
                  value={videoUrl}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                  fullWidth
                  helperText="Supported: YouTube, Vimeo, MP4 links"
                />
                
                {videoUrl && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" gutterBottom>
                      Preview:
                    </Typography>
                    {getVideoEmbedCode(videoUrl)}
                  </Box>
                )}
              </TabPanel>
            </>
          )}

          <Box display="flex" gap={2}>
            <TextField
              label="Width"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              placeholder="e.g., 100%, 500px"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Height"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="e.g., auto, 300px"
              sx={{ flex: 1 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={type === 'video' ? !selectedVideo && !videoUrl : !selectedVideo}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Media Library Browser Component
const MediaLibraryBrowser = ({ courseId, onSelectImage, selectedImage, mediaType = 'image' }) => {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getAccessTokenSilently } = useAuth0();
  const { orgUser } = useOrgUser();

  useEffect(() => {
    fetchMediaLibrary();
  }, [courseId, mediaType]);

  const fetchMediaLibrary = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently({
        audience: 'https://qallix-api',
      });
      const orgName = orgUser.orgName;

      try {
        const manifestResponse = await getObject(
          token,
          {
            organization: orgName,
            bucketName: 'qallix-storage',
            key: `QallixAcademy/SoftwareCourses/${courseId}/media/manifest.json`
          }
        );

        if (manifestResponse.statusCode === 200) {
          const decodedContent = atob(manifestResponse.fileContent);
          const manifest = JSON.parse(decodedContent);
          
          // Filter by media type
          const filteredItems = (manifest.files || []).filter(file =>
            mediaType === 'image' ? file.type.startsWith('image/') : file.type.startsWith('video/')
          );
          
          setMediaItems(filteredItems);
        }
      } catch (error) {
        setMediaItems([]);
        
        // For demo purposes, add mock media based on type
        if (mediaType === 'image') {
          setMediaItems([
            {
              name: 'demo-image-1.jpg',
              key: 'QallixAcademy/SoftwareCourses/demo/media/demo-image-1.jpg',
              type: 'image/jpeg',
              size: 102400,
              uploadedAt: new Date().toISOString()
            },
            {
              name: 'demo-image-2.png',
              key: 'QallixAcademy/SoftwareCourses/demo/media/demo-image-2.png',
              type: 'image/png',
              size: 204800,
              uploadedAt: new Date().toISOString()
            }
          ]);
        } else {
          setMediaItems([
            {
              name: 'demo-video-1.mp4',
              key: 'QallixAcademy/SoftwareCourses/demo/media/demo-video-1.mp4',
              type: 'video/mp4',
              size: 5242880,
              uploadedAt: new Date().toISOString()
            },
            {
              name: 'demo-video-2.mp4',
              key: 'QallixAcademy/SoftwareCourses/demo/media/demo-video-2.mp4',
              type: 'video/mp4',
              size: 10485760,
              uploadedAt: new Date().toISOString()
            }
          ]);
        }
      }
    } catch (error) {
      console.error('Error fetching media library:', error);
      setMediaItems([]);
    } finally {
      setLoading(false);
    }
  };

  const getPresignedUrl = async (file) => {
    try {
      const token = await getAccessTokenSilently({
        audience: 'https://qallix-api',
      });
      const orgName = orgUser.orgName;

      const data = {
        organization: orgName,
        draftItems: [
          {
            bucketName: 'qallix-storage',
            key: file.key,
            expires: 3600
          }
        ]
      };

      const response = await getSignedUrl(token, data);

      if (response.statusCode === 200 && response.data && response.data.length > 0) {
        return response.data[0];
      }
      throw new Error('Failed to generate presigned URL');
    } catch (error) {
      console.error('Error generating presigned URL:', error);
      // Return a placeholder for demo purposes
      return mediaType === 'image' ? 'https://via.placeholder.com/150' : '';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading {mediaType}s...</Typography>
      </Box>
    );
  }

  if (mediaItems.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>No {mediaType}s found in the media library.</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Upload {mediaType}s using the Media Library tab first.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {mediaItems.map((media, index) => (
        <Grid item xs={6} sm={4} md={3} key={index}>
          <MediaThumbnail
            media={media}
            onSelect={onSelectImage}
            isSelected={selectedImage === media.key}
            getPresignedUrl={getPresignedUrl}
            mediaType={mediaType}
          />
        </Grid>
      ))}
    </Grid>
  );
};

// Media Thumbnail Component (updated for videos)
const MediaThumbnail = ({ media, onSelect, isSelected, getPresignedUrl, mediaType }) => {
  const [mediaUrl, setMediaUrl] = useState('');

  useEffect(() => {
    const loadMedia = async () => {
      const url = await getPresignedUrl(media);
      setMediaUrl(url || '');
    };

    loadMedia();
  }, [media, getPresignedUrl]);

  const handleClick = () => {
    onSelect(media.key);
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? 'primary.main' : 'divider',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 3
        }
      }}
      onClick={handleClick}
    >
      <Box sx={{ 
        height: 120, 
        overflow: 'hidden', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: mediaType === 'video' ? 'grey.100' : 'transparent'
      }}>
        {mediaUrl ? (
          mediaType === 'image' ? (
            <img
              src={mediaUrl}
              alt={media.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Videocam sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="caption" color="textSecondary">
                Video
              </Typography>
            </Box>
          )
        ) : (
          mediaType === 'image' ? (
            <ImageIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          ) : (
            <Videocam sx={{ fontSize: 40, color: 'text.secondary' }} />
          )
        )}
      </Box>
      <CardContent sx={{ p: 1 }}>
        <Typography variant="body2" noWrap title={media.name}>
          {media.name}
        </Typography>
        <Typography variant="caption" color="textSecondary" title={media.key}>
          {media.key.length > 20 ? `${media.key.substring(0, 20)}...` : media.key}
        </Typography>
      </CardContent>
    </Card>
  );
};

const ContentTypeSelector = ({ onSelect, isNested = false }) => {
  // Don't show dropdown option for nested content
  const contentTypes = [
    { type: 'text', icon: <TextFields />, label: 'Text', description: 'Add formatted text content with headings and lists' },
    { type: 'video', icon: <Videocam />, label: 'Video', description: 'Embed video content from URLs or library' },
    { type: 'image', icon: <ImageIcon />, label: 'Image', description: 'Insert images to illustrate concepts' },
    ...(isNested ? [] : [{ type: 'dropdown', icon: <ExpandMore />, label: 'Lesson Dropdown', description: 'Create collapsible sections with titles' }]),
  ];

  return (
    <Box sx={{ p: 1 }}>
      <Grid container spacing={2}>
        {contentTypes.map((item) => (
          <Grid item xs={6} sm={4} md={3} key={item.type}>
            <Card
              variant="outlined"
              sx={{
                p: 2,
                cursor: 'pointer',
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease-in-out',
                border: '1px solid',
                borderColor: 'grey.200',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 2,
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50',
                }
              }}
              onClick={() => onSelect(item.type)}
            >
              <Box
                sx={{
                  color: 'primary.main',
                  mb: 1,
                  fontSize: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {item.icon}
              </Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                {item.label}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2, fontFamily: "'Satoshi', sans-serif" }}>
                {item.description}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Rich Text Editor Component with React Quill
const RichTextEditor = React.memo(({ value, onChange, placeholder }) => {
  const quillRef = useRef(null);
  
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean']
    ],
    clipboard: {
      // This helps prevent extra line breaks
      matchVisual: false,
    }
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link'
  ];

  const handleChange = (content, delta, source, editor) => {
    // Get the text content to check if it's empty
    const textContent = editor.getText().trim();
    
    // If the editor is effectively empty, pass an empty string
    if (!textContent && (content === '<p><br></p>' || content === '<p></p>')) {
      onChange('');
    } else {
      onChange(content);
    }
  };

  return (
    <Box sx={{ border: '1px solid #ccc', borderRadius: '4px' }}>
      <ReactQuill
        ref={quillRef}
        value={value || ''}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        theme="snow"
        style={{ minHeight: '250px' }}
      />
    </Box>
  );
});

// Secure Image Component for displaying images from private buckets
const SecureImage = ({ imageKey, width, height, alt, caption, ...props }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const { orgUser } = useOrgUser();

  useEffect(() => {
    const generateSignedUrl = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently({
          audience: 'https://qallix-api',
        });
        const orgName = orgUser.orgName;

        const data = {
          organization: orgName,
          draftItems: [
            {
              bucketName: 'qallix-storage',
              key: imageKey,
              expires: 3600 // 1 hour expiration
            }
          ]
        };

        const response = await getSignedUrl(token, data);

        if (response.statusCode === 200 && response.data && response.data.length > 0) {
          setImageUrl(response.data[0]);
          setError(false);
        } else {
          setError(true);
        }
      } catch (error) {
        console.error('Error generating signed URL:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (imageKey) {
      generateSignedUrl();
    }
  }, [imageKey, getAccessTokenSilently, orgUser]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
        <LinearProgress sx={{ width: '100%', mb: 1 }} />
        <span>Loading image...</span>
      </Box>
    );
  }

  if (error || !imageUrl) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed #ccc',
        borderRadius: 1,
        p: 2,
        backgroundColor: '#f5f5f5',
        minHeight: '200px'
      }}>
        <span>Image unavailable</span>
        <span style={{ fontSize: '0.8rem', color: '#666' }}>Failed to load image</span>
      </Box>
    );
  }

  return (
    <Box>
      <img
        src={imageUrl}
        alt={alt || ''}
        style={{
          width: width || '100%',
          height: height || 'auto',
          objectFit: 'contain',
          borderRadius: 4
        }}
        {...props}
      />
      {caption && (
        <Box sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary', fontSize: '0.9rem' }}>
          {caption}
        </Box>
      )}
    </Box>
  );
};

// Secure Video Component for displaying videos from private buckets
const SecureVideo = ({ videoKey, width, height, alt, ...props }) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const { orgUser } = useOrgUser();

  useEffect(() => {
    const generateSignedUrl = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently({
          audience: 'https://qallix-api',
        });
        const orgName = orgUser.orgName;

        const data = {
          organization: orgName,
          draftItems: [
            {
              bucketName: 'qallix-storage',
              key: videoKey,
              expires: 3600 // 1 hour expiration
            }
          ]
        };

        const response = await getSignedUrl(token, data);

        if (response.statusCode === 200 && response.data && response.data.length > 0) {
          setVideoUrl(response.data[0]);
          setError(false);
        } else {
          setError(true);
        }
      } catch (error) {
        console.error('Error generating signed URL:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (videoKey) {
      generateSignedUrl();
    }
  }, [videoKey, getAccessTokenSilently, orgUser]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
        <LinearProgress sx={{ width: '100%', mb: 1 }} />
        <span>Loading video...</span>
      </Box>
    );
  }

  if (error || !videoUrl) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed #ccc',
        borderRadius: 1,
        p: 2,
        backgroundColor: '#f5f5f5',
        minHeight: '200px'
      }}>
        <Videocam sx={{ fontSize: 48, mb: 1, color: 'text.secondary' }} />
        <span>Video unavailable</span>
        <span style={{ fontSize: '0.8rem', color: '#666' }}>Failed to load video</span>
      </Box>
    );
  }

  return (
    <Box>
      <video
        controls
        width={width || '100%'}
        height={height || 'auto'}
        style={{
          borderRadius: 4,
          maxWidth: '100%'
        }}
        {...props}
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      {alt && (
        <Box sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary', fontSize: '0.9rem' }}>
          {alt}
        </Box>
      )}
    </Box>
  );
};

// Lesson Dropdown Component - Fixed with media dialog support for nested content
const LessonDropdown = React.memo(({
  item,
  index,
  updateItem,
  removeContentItem,
  provided,
  snapshot,
  courseId,
}) => {
  const [isOpen, setIsOpen] = useState(item.isOpen || false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [editingNestedIndex, setEditingNestedIndex] = useState(null);
  const [mediaType, setMediaType] = useState('');

  const toggleDropdown = useCallback(() => {
    const newState = !isOpen;
    setIsOpen(newState);
    updateItem(index, 'isOpen', newState);
  }, [isOpen, updateItem, index]);

  const handleTitleChange = useCallback((e) => {
    updateItem(index, 'title', e.target.value);
  }, [updateItem, index]);

  const handleAddNestedContent = useCallback((type) => {
    let newNestedItem;
    const nextOrder = item.content ? item.content.length + 1 : 1;

    switch (type) {
      case 'text':
        newNestedItem = { type, id: Date.now().toString(), value: '', order: nextOrder };
        break;
      case 'video':
        newNestedItem = {
          type,
          id: Date.now().toString(),
          url: '',
          imageKey: '',
          width: '100%',
          height: 'auto',
          caption: '',
          order: nextOrder
        };
        break;
      case 'image':
        newNestedItem = {
          type,
          id: Date.now().toString(),
          imageKey: '',
          width: '100%',
          height: 'auto',
          alt: '',
          caption: '',
          order: nextOrder
        };
        break;
      default:
        newNestedItem = { type, id: Date.now().toString(), value: '', order: nextOrder };
    }

    const updatedContent = item.content ? [...item.content, newNestedItem] : [newNestedItem];
    updateItem(index, 'content', updatedContent);
  }, [item.content, updateItem, index]);

  const handleUpdateNestedContent = useCallback((nestedIndex, field, value) => {
    const updatedContent = [...item.content];
    updatedContent[nestedIndex] = { ...updatedContent[nestedIndex], [field]: value };
    updateItem(index, 'content', updatedContent);
  }, [item.content, updateItem, index]);

  const handleRemoveNestedContent = useCallback((nestedIndex) => {
    const updatedContent = item.content.filter((_, i) => i !== nestedIndex);
    updateItem(index, 'content', updatedContent);
  }, [item.content, updateItem, index]);

  const openMediaDialog = useCallback((nestedIndex, type) => {
    setEditingNestedIndex(nestedIndex);
    setMediaType(type);
    setMediaDialogOpen(true);
  }, []);

  const handleMediaConfig = useCallback((config) => {
    if (editingNestedIndex !== null) {
      const updatedContent = [...item.content];
      const nestedItem = updatedContent[editingNestedIndex];

      if (mediaType === 'image') {
        updatedContent[editingNestedIndex] = {
          ...nestedItem,
          imageKey: config.imageKey,
          url: '', // Clear URL when using image from library
          width: config.width,
          height: config.height
        };
      } else if (mediaType === 'video') {
        updatedContent[editingNestedIndex] = {
          ...nestedItem,
          imageKey: config.imageKey, // For library videos
          url: config.url, // For external URLs
          width: config.width,
          height: config.height
        };
      }

      updateItem(index, 'content', updatedContent);
    }
    setMediaDialogOpen(false);
    setEditingNestedIndex(null);
  }, [editingNestedIndex, item.content, mediaType, updateItem, index]);

  const renderNestedContentItem = useCallback((nestedItem, nestedIndex) => {
    switch (nestedItem.type) {
      case 'text':
        return (
          <Card key={nestedItem.id} sx={{ mb: 2, ml: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="subtitle2" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                  Text Content
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <IconButton onClick={() => handleRemoveNestedContent(nestedIndex)} size="small">
                  <Delete />
                </IconButton>
              </Box>
              <RichTextEditor
                value={nestedItem.value || ''}
                onChange={(value) => handleUpdateNestedContent(nestedIndex, 'value', value)}
                placeholder="Enter text content..."
              />
            </CardContent>
          </Card>
        );
      case 'video':
        return (
          <Card key={nestedItem.id} sx={{ mb: 2, ml: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="subtitle2" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                  Video Content
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <IconButton onClick={() => handleRemoveNestedContent(nestedIndex)} size="small">
                  <Delete />
                </IconButton>
              </Box>
              {nestedItem.url || nestedItem.imageKey ? (
                <Box mb={2}>
                  {/* Show embedded video for URLs */}
                  {nestedItem.url && (
                    <Box mb={2}>
                      {getVideoEmbedCode(nestedItem.url, nestedItem.width, nestedItem.height)}
                    </Box>
                  )}
                  
                  {/* Show uploaded video from library */}
                  {nestedItem.imageKey && !nestedItem.url && (
                    <Box mb={2}>
                      <SecureVideo
                        videoKey={nestedItem.imageKey}
                        width={nestedItem.width}
                        height={nestedItem.height}
                        alt={nestedItem.alt || ''}
                      />
                    </Box>
                  )}
                  
                  <TextField
                    fullWidth
                    label="Video Caption"
                    value={nestedItem.caption || ''}
                    onChange={(e) => handleUpdateNestedContent(nestedIndex, 'caption', e.target.value)}
                    placeholder="Add a caption for the video"
                    sx={{ mt: 1 }}
                  />
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Videocam sx={{ fontSize: 48, mb: 1 }} />
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }}>No video configured</Typography>
                </Box>
              )}
              <Button
                variant="outlined"
                onClick={() => openMediaDialog(nestedIndex, 'video')}
                startIcon={<Edit />}
                size="small"
                sx={{
                  backgroundColor: "#332872",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#241e52" },
                  fontFamily: "'Satoshi', sans-serif",
                }}
              >
                {nestedItem.url || nestedItem.imageKey ? 'Edit Video' : 'Configure Video'}
              </Button>
            </CardContent>
          </Card>
        );
      case 'image':
        return (
          <Card key={nestedItem.id} sx={{ mb: 2, ml: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="subtitle2" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                  Image Content
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <IconButton onClick={() => handleRemoveNestedContent(nestedIndex)} size="small">
                  <Delete />
                </IconButton>
              </Box>
              {nestedItem.imageKey ? (
                <Box mb={2}>
                  <SecureImage
                    imageKey={nestedItem.imageKey}
                    width={nestedItem.width}
                    height={nestedItem.height}
                    alt={nestedItem.alt || ''}
                    caption={nestedItem.caption || ''}
                  />
                  <TextField
                    fullWidth
                    label="Alt Text"
                    value={nestedItem.alt || ''}
                    onChange={(e) => handleUpdateNestedContent(nestedIndex, 'alt', e.target.value)}
                    placeholder="Describe the image for accessibility"
                    sx={{ mt: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Image Caption"
                    value={nestedItem.caption || ''}
                    onChange={(e) => handleUpdateNestedContent(nestedIndex, 'caption', e.target.value)}
                    placeholder="Add a caption for the image"
                    sx={{ mt: 1 }}
                  />
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <ImageIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }}>No image configured</Typography>
                </Box>
              )}
              <Button
                variant="outlined"
                onClick={() => openMediaDialog(nestedIndex, 'image')}
                startIcon={<Edit />}
                size="small"
                sx={{
                  backgroundColor: "#332872",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#241e52" },
                  fontFamily: "'Satoshi', sans-serif",
                }}
              >
                {nestedItem.imageKey ? 'Edit Image' : 'Configure Image'}
              </Button>
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  }, [handleUpdateNestedContent, handleRemoveNestedContent, openMediaDialog]);

  return (
    <Card
      ref={provided.innerRef}
      {...provided.draggableProps}
      sx={{
        mb: 2,
        backgroundColor: snapshot.isDragging ? 'primary.light' : 'background.paper'
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" mb={1}>
          <Box {...provided.dragHandleProps}>
            <DragIndicator sx={{ mr: 1, cursor: 'grab' }} />
          </Box>
          <Box display="flex" alignItems="center" sx={{ mr: 2 }}>
            <Numbers sx={{ fontSize: 18, mr: 0.5, color: 'primary.main' }} />
            <Typography variant="body2" color="primary.main" fontWeight="bold">
              {item.order}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
            Lesson Dropdown
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={() => removeContentItem(index)}>
            <Delete />
          </IconButton>
        </Box>

        <TextField
          fullWidth
          label="Dropdown Title"
          value={item.title || ''}
          onChange={handleTitleChange}
          placeholder="Enter the title for this dropdown section"
          sx={{ mb: 2 }}
        />

        <Button
          variant="outlined"
          onClick={toggleDropdown}
          endIcon={isOpen ? <ExpandLess /> : <ExpandMore />}
          sx={{
            backgroundColor: "#332872",
            color: "#fff",
            "&:hover": { backgroundColor: "#241e52" },
            fontFamily: "'Satoshi', sans-serif",
            mb: 2
          }}
        >
          {isOpen ? 'Collapse' : 'Expand'} Dropdown
        </Button>

        {isOpen && (
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: "'Satoshi', sans-serif", mt: 2 }}>
              Dropdown Content
            </Typography>

            {item.content && item.content.length > 0 ? (
              <Box>
                {item.content.map((nestedItem, nestedIndex) =>
                  renderNestedContentItem(nestedItem, nestedIndex)
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }} variant="body2" gutterBottom>
                  No content in this dropdown yet
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 2, ml: 3, mb: 3 }}>
              {/* Pass isNested prop to hide dropdown option */}
              <ContentTypeSelector onSelect={handleAddNestedContent} isNested={true} />
            </Box>
          </Box>
        )}

        {/* Media Dialog for nested content */}
        <MediaConfigDialog
          open={mediaDialogOpen}
          onClose={() => setMediaDialogOpen(false)}
          onConfirm={handleMediaConfig}
          type={mediaType}
          initialImageKey={editingNestedIndex !== null ?
            (mediaType === 'image' ? 
              item.content[editingNestedIndex]?.imageKey || '' : 
              item.content[editingNestedIndex]?.imageKey || '')
            : ''}
          initialUrl={editingNestedIndex !== null && mediaType === 'video' ? 
            item.content[editingNestedIndex]?.url || '' : ''}
          initialWidth={editingNestedIndex !== null ? item.content[editingNestedIndex]?.width || '100%' : '100%'}
          initialHeight={editingNestedIndex !== null ? item.content[editingNestedIndex]?.height || 'auto' : 'auto'}
          courseId={courseId}
        />
      </CardContent>
    </Card>
  );
});

const ContentEditor = ({ content, onChange, courseId, droppableId = generateId(), onSave }) => {
  // Initialize content with order property
  const initializeContentWithOrder = (contentArray) => {
    return contentArray ? contentArray.map((item, index) => ({
      ...item,
      order: item.order !== undefined ? item.order : index + 1
    })) : [];
  };

  const [localContent, setLocalContent] = useState(initializeContentWithOrder(content));
  const [tabValue, setTabValue] = useState(0);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [editingMediaIndex, setEditingMediaIndex] = useState(null);
  const [mediaType, setMediaType] = useState('');

  // Update parent when local content changes
  useEffect(() => {
    onChange(localContent);
  }, [localContent]);

  const updateContent = useCallback((newContent) => {
    // Ensure all items have the correct order based on their position
    const orderedContent = newContent.map((item, index) => ({
      ...item,
      order: index + 1
    }));
    setLocalContent(orderedContent);
  }, []);

  const updateItem = useCallback((index, field, value) => {
    const updatedContent = [...localContent];
    updatedContent[index] = { ...updatedContent[index], [field]: value };
    updateContent(updatedContent);
  }, [localContent, updateContent]);

  const addContentItem = useCallback((type) => {
    let newItem;
    const nextOrder = localContent.length + 1;

    switch (type) {
      case 'text':
        newItem = { type, id: Date.now().toString(), value: '', order: nextOrder };
        break;
      case 'video':
        newItem = {
          type,
          id: Date.now().toString(),
          url: '',
          imageKey: '',
          width: '100%',
          height: 'auto',
          caption: '',
          order: nextOrder
        };
        break;
      case 'image':
        newItem = {
          type,
          id: Date.now().toString(),
          imageKey: '',
          width: '100%',
          height: 'auto',
          alt: '',
          caption: '',
          order: nextOrder
        };
        break;
      case 'dropdown':
        newItem = {
          type,
          id: Date.now().toString(),
          title: 'New Lesson Dropdown',
          content: [], // This will hold nested content items
          isOpen: false, // Controls whether dropdown is expanded
          order: nextOrder
        };
        break;
      default:
        newItem = { type, id: Date.now().toString(), value: '', order: nextOrder };
    }

    updateContent([...localContent, newItem]);
    setTabValue(0);
  }, [localContent, updateContent]);

  const removeContentItem = useCallback((index) => {
    updateContent(localContent.filter((_, i) => i !== index));
  }, [localContent, updateContent]);

  const openMediaDialog = useCallback((index, type) => {
    setEditingMediaIndex(index);
    setMediaType(type);
    setMediaDialogOpen(true);
  }, []);

  const handleMediaConfig = useCallback((config) => {
    if (editingMediaIndex !== null) {
      const updatedContent = [...localContent];
      const item = updatedContent[editingMediaIndex];

      if (mediaType === 'image') {
        updatedContent[editingMediaIndex] = {
          ...item,
          imageKey: config.imageKey,
          url: '', // Clear URL when using image from library
          width: config.width,
          height: config.height
        };
      } else if (mediaType === 'video') {
        updatedContent[editingMediaIndex] = {
          ...item,
          imageKey: config.imageKey, // For library videos
          url: config.url, // For external URLs
          width: config.width,
          height: config.height
        };
      }

      updateContent(updatedContent);
    }
    setMediaDialogOpen(false);
    setEditingMediaIndex(null);
  }, [editingMediaIndex, localContent, mediaType, updateContent]);

  const onDragEnd = useCallback((result) => {
    if (!result.destination) return;

    const items = Array.from(localContent);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    updateContent(items);
  }, [localContent, updateContent]);

  // Add useEffect to handle external save triggers
  useEffect(() => {
    const handleExternalSave = () => {
      const contentJSON = JSON.stringify(localContent, null, 2);
      if (onSave) onSave();
    };

    window.addEventListener('triggerSave', handleExternalSave);

    return () => {
      window.removeEventListener('triggerSave', handleExternalSave);
    };
  }, [localContent, onSave]);

  const renderContentItem = useCallback((item, index) => {
    switch (item.type) {
      case 'text':
        return (
          <Draggable key={item.id} draggableId={item.id} index={index}>
            {(provided, snapshot) => (
              <Card
                ref={provided.innerRef}
                {...provided.draggableProps}
                sx={{
                  mb: 2,
                  backgroundColor: snapshot.isDragging ? 'primary.light' : 'background.paper'
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Box {...provided.dragHandleProps}>
                      <DragIndicator sx={{ mr: 1, cursor: 'grab' }} />
                    </Box>
                    <Box display="flex" alignItems="center" sx={{ mr: 2 }}>
                      <Numbers sx={{ fontSize: 18, mr: 0.5, color: 'primary.main' }} />
                      <Typography variant="body2" color="primary.main" fontWeight="bold">
                        {item.order}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontFamily: "'Satoshi', sans-serif" }}>Rich Text Content</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton onClick={() => removeContentItem(index)}>
                      <Delete />
                    </IconButton>
                  </Box>
                  <RichTextEditor
                    value={item.value || ''}
                    onChange={(value) => updateItem(index, 'value', value)}
                    placeholder="Enter text content with headings, lists, and formatting..."
                  />
                </CardContent>
              </Card>
            )}
          </Draggable>
        );

      case 'video':
        return (
          <Draggable key={item.id} draggableId={item.id} index={index}>
            {(provided, snapshot) => (
              <Card
                ref={provided.innerRef}
                {...provided.draggableProps}
                sx={{
                  mb: 2,
                  backgroundColor: snapshot.isDragging ? 'primary.light' : 'background.paper'
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Box {...provided.dragHandleProps}>
                      <DragIndicator sx={{ mr: 1, cursor: 'grab' }} />
                    </Box>
                    <Box display="flex" alignItems="center" sx={{ mr: 2 }}>
                      <Numbers sx={{ fontSize: 18, mr: 0.5, color: 'primary.main' }} />
                      <Typography variant="body2" color="primary.main" fontWeight="bold">
                        {item.order}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                      Video Content
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton onClick={() => removeContentItem(index)}>
                      <Delete />
                    </IconButton>
                  </Box>

                  {item.url || item.imageKey ? (
                    <Box mb={2}>
                      {/* Show embedded video for URLs */}
                      {item.url && (
                        <Box mb={2}>
                          {getVideoEmbedCode(item.url, item.width, item.height)}
                        </Box>
                      )}
                      
                      {/* Show uploaded video from library */}
                      {item.imageKey && !item.url && (
                        <Box mb={2}>
                          <SecureVideo
                            videoKey={item.imageKey}
                            width={item.width}
                            height={item.height}
                            alt={item.alt || ''}
                          />
                        </Box>
                      )}
                      
                      <TextField
                        fullWidth
                        label="Video Caption"
                        value={item.caption || ''}
                        onChange={(e) => updateItem(index, 'caption', e.target.value)}
                        placeholder="Add a caption for the video"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Videocam sx={{ fontSize: 48, mb: 1 }} />
                      <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                        No video configured
                      </Typography>
                    </Box>
                  )}

                  <Button
                    variant="outlined"
                    onClick={() => openMediaDialog(index, 'video')}
                    startIcon={<Edit />}
                    sx={{
                      backgroundColor: "#332872",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#241e52" },
                      fontFamily: "'Satoshi', sans-serif",
                    }}
                  >
                    {item.url || item.imageKey ? 'Edit Video' : 'Configure Video'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </Draggable>
        );

      case 'image':
        return (
          <Draggable key={item.id} draggableId={item.id} index={index}>
            {(provided, snapshot) => (
              <Card
                ref={provided.innerRef}
                {...provided.draggableProps}
                sx={{
                  mb: 2,
                  backgroundColor: snapshot.isDragging ? 'primary.light' : 'background.paper'
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Box {...provided.dragHandleProps}>
                      <DragIndicator sx={{ mr: 1, cursor: 'grab' }} />
                    </Box>
                    <Box display="flex" alignItems="center" sx={{ mr: 2 }}>
                      <Numbers sx={{ fontSize: 18, mr: 0.5, color: 'primary.main' }} />
                      <Typography variant="body2" color='primary.main' fontWeight="bold">
                        {item.order}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontFamily: "'Satoshi', sans-serif" }}>Image Content</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton onClick={() => removeContentItem(index)}>
                      <Delete />
                    </IconButton>
                  </Box>

                  {item.imageKey ? (
                    <Box mb={2}>
                      <SecureImage
                        imageKey={item.imageKey}
                        width={item.width}
                        height={item.height}
                        alt={item.alt || ''}
                        caption={item.caption || ''}
                      />
                      <TextField
                        fullWidth
                        label="Alt Text"
                        value={item.alt || ''}
                        onChange={(e) => updateItem(index, 'alt', e.target.value)}
                        placeholder="Describe the image for accessibility"
                        sx={{ mt: 1 }}
                      />
                      <TextField
                        fullWidth
                        label="Image Caption"
                        value={item.caption || ''}
                        onChange={(e) => updateItem(index, 'caption', e.target.value)}
                        placeholder="Add a caption for the image"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <ImageIcon sx={{ fontSize: 48, mb: 1 }} />
                      <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }}>No image configured</Typography>
                    </Box>
                  )}

                  <Button
                    variant="outlined"
                    onClick={() => openMediaDialog(index, 'image')}
                    startIcon={<Edit />}
                    sx={{
                      backgroundColor: "#332872",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#241e52" },
                      fontFamily: "'Satoshi', sans-serif",
                    }}
                  >
                    {item.imageKey ? 'Edit Image' : 'Configure Image'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </Draggable>
        );
      
      case 'dropdown':
        return (
          <Draggable key={item.id} draggableId={item.id} index={index}>
            {(provided, snapshot) => (
              <LessonDropdown
                item={item}
                index={index}
                updateItem={updateItem}
                removeContentItem={removeContentItem}
                provided={provided}
                snapshot={snapshot}
                courseId={courseId}
              />
            )}
          </Draggable>
        );
      default:
        return null;
    }
  }, [updateItem, removeContentItem, openMediaDialog, courseId]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontFamily: "'Satoshi', sans-serif" }}>
          Content Editor
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ fontFamily: "'Satoshi', sans-serif" }}
        >
          <Tab label="Content" sx={{ fontFamily: "'Satoshi', sans-serif" }} />
          <Tab label="Add New" sx={{ fontFamily: "'Satoshi', sans-serif" }} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {localContent.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }} variant="body1" gutterBottom>
              No content added yet
            </Typography>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }} variant="body2">
              Click on the "Add New" tab to add content
            </Typography>
          </Box>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={droppableId}>
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {localContent.map((item, index) => renderContentItem(item, index))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ContentTypeSelector onSelect={addContentItem} />
      </TabPanel>

      <MediaConfigDialog
        open={mediaDialogOpen}
        onClose={() => setMediaDialogOpen(false)}
        onConfirm={handleMediaConfig}
        type={mediaType}
        initialImageKey={editingMediaIndex !== null ?
          (mediaType === 'image' ? 
            localContent[editingMediaIndex]?.imageKey || '' : 
            localContent[editingMediaIndex]?.imageKey || '')
          : ''}
        initialUrl={editingMediaIndex !== null && mediaType === 'video' ? 
          localContent[editingMediaIndex]?.url || '' : ''}
        initialWidth={editingMediaIndex !== null ? localContent[editingMediaIndex]?.width || '100%' : '100%'}
        initialHeight={editingMediaIndex !== null ? localContent[editingMediaIndex]?.height || 'auto' : 'auto'}
        courseId={courseId}
      />
    </Box>
  );
};

export default ContentEditor;